import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TestInterface } from '../components';
import type { TestType } from '../types';

const PVTTest: React.FC = () => {
  const { testId } = useParams();
  const location = useLocation();
  const { user, userRole, isAuthenticated } = useAuth();
  
  const [testConfig, setTestConfig] = useState<{
    testDuration: number;
    userId?: string;
    sessionId?: string;
    testType?: TestType;
    isMockup?: boolean;
  } | null>(null);

  // Initialize test configuration
  useEffect(() => {
    // For authenticated users, get config from location state or use defaults
    if (isAuthenticated && user) {
      const locationState = location.state as any;
      if (locationState?.testDuration) {
        // Use config from TestSetup navigation
        setTestConfig({
          testDuration: locationState.testDuration, // Already in ms
          userId: locationState.userId || user.id,
          testType: locationState.testType,
          isMockup: locationState.isMockup,
        });
      } else {
        // Default config for direct navigation
        const defaultDuration = 600000; // 10 minutes in milliseconds
        setTestConfig({
          testDuration: defaultDuration,
          userId: user.id,
        });
      }
    } else {
      // For guest users, check sessionStorage
      const storedUserInfo = sessionStorage.getItem('userInfo');
      if (storedUserInfo) {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        const guestDurationMs = parsedUserInfo.testDuration || 600000;
        setTestConfig({
          testDuration: guestDurationMs,
          sessionId: parsedUserInfo.sessionId
        });
      } else {
        // Set default guest config if no stored info
        setTestConfig({
          testDuration: 600000, // 10 minutes default
          sessionId: 'guest-' + Date.now()
        });
      }
    }
  }, [isAuthenticated, user, location.state]);

  // Determine user type based on authentication and role
  const getUserType = (): 'admin' | 'guest' => {
    if (!isAuthenticated) return 'guest';
    return 'admin';
  };

  if (!testConfig) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
        <div className="text-navy-200">Loading test configuration...</div>
      </div>
    );
  }

  return (
    <TestInterface
      testId={testId}
      userType={getUserType()}
      testConfig={testConfig}
    />
  );
};

export default PVTTest;
